import type {
  AdminCustomField,
  AdminCustomFieldsResponse,
  CreateAdminCustomFieldBody,
  UpdateAdminCustomFieldBody,
} from '@/api/(admin)/custom-information-fields/types';
import { API_ROOT_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';

/**
 * Pure RPC for the admin custom-information-fields page, all on the migrated
 * `/api/admin/c2c-default-questions` (`API_ROOT_URL`).
 */

/**
 * The list — `GET /api/admin/c2c-default-questions`, the migrated route. Takes
 * a pre-built query string; paging, sort and filter live in the hook.
 */
export async function fetchAdminCustomFields(
  qs: string,
): Promise<AdminCustomFieldsResponse> {
  return apiFetch<AdminCustomFieldsResponse>(
    `/api/admin/c2c-default-questions?${qs}`,
    {},
    API_ROOT_URL,
  );
}

/**
 * One field with its options — `GET /api/admin/c2c-default-questions/:id`,
 * the migrated route. Answers the `{ data }` envelope; the hook unwraps it.
 * 404 for a removed or company-owned question.
 */
export async function fetchAdminCustomField(
  id: string,
): Promise<{ data: AdminCustomField }> {
  return apiFetch<{ data: AdminCustomField }>(
    `/api/admin/c2c-default-questions/${encodeURIComponent(id)}`,
    {},
    API_ROOT_URL,
  );
}

/**
 * Creates one field — `POST /api/admin/c2c-default-questions`, the migrated
 * route. Answers 201 with the stored field in a `{ data }` envelope; 409
 * `api_field_identifier_is_already_in_use` on a duplicate API identifier.
 */
export async function createAdminCustomField(
  body: CreateAdminCustomFieldBody,
): Promise<{ data: AdminCustomField }> {
  return apiFetch<{ data: AdminCustomField }>(
    '/api/admin/c2c-default-questions',
    { method: 'POST', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

/**
 * Edits a field's title and/or hint — `PUT /api/admin/c2c-default-questions/:id`,
 * the migrated route. Answers the stored field in a `{ data }` envelope.
 * 404 for a removed or company-owned question; 400 for any key but `title`
 * and `hintText`.
 */
export async function updateAdminCustomField(
  id: string,
  body: UpdateAdminCustomFieldBody,
): Promise<{ data: AdminCustomField }> {
  return apiFetch<{ data: AdminCustomField }>(
    `/api/admin/c2c-default-questions/${encodeURIComponent(id)}`,
    { method: 'PUT', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

/**
 * Soft-deletes every common field given (`is_removed = 1`) —
 * `PUT /api/admin/c2c-default-questions/bulk-remove`, the migrated route.
 * Answers 204; 404 when none of the ids names a live common field.
 */
export async function bulkRemoveAdminCustomFields(
  ids: string[],
): Promise<void> {
  return apiFetch<void>(
    '/api/admin/c2c-default-questions/bulk-remove',
    {
      method: 'PUT',
      body: JSON.stringify({ ids }),
    },
    API_ROOT_URL,
  );
}
